import type { Breadcrumb, ConsoleEntry, NetworkError } from "./types.js";

const CONSOLE_MAX = 50;
const NETWORK_MAX = 30;
const BREADCRUMB_MAX = 20;

function ring<T>(max: number) {
  const arr: T[] = [];
  return {
    push(v: T) {
      arr.push(v);
      if (arr.length > max) arr.splice(0, arr.length - max);
    },
    snapshot(): T[] {
      return arr.slice();
    },
  };
}

function detectOS(ua: string): string {
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iOS/i.test(ua)) return "iOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "Unknown";
}
function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Unknown";
}

export function createCapture() {
  const consoleRing = ring<ConsoleEntry>(CONSOLE_MAX);
  const networkRing = ring<NetworkError>(NETWORK_MAX);
  const breadcrumbs = ring<Breadcrumb>(BREADCRUMB_MAX);

  // --- console hooks ---
  const levels: Array<"log" | "info" | "warn" | "error" | "debug"> = [
    "log",
    "info",
    "warn",
    "error",
    "debug",
  ];
  for (const lvl of levels) {
    const orig = console[lvl] as (...args: unknown[]) => void;
    console[lvl] = ((...args: unknown[]) => {
      try {
        consoleRing.push({
          level: lvl,
          message: args
            .map((a) => {
              if (a instanceof Error) return `${a.name}: ${a.message}`;
              if (typeof a === "string") return a;
              try {
                return JSON.stringify(a);
              } catch {
                return String(a);
              }
            })
            .join(" ")
            .slice(0, 2000),
          timestamp: Date.now(),
        });
      } catch {
        // noop
      }
      orig.apply(console, args);
    }) as typeof console.log;
  }

  // --- fetch hook ---
  const origFetch = window.fetch?.bind(window);
  if (origFetch) {
    window.fetch = async (...args) => {
      const t0 = performance.now();
      try {
        const res = await origFetch(...args);
        if (!res.ok) {
          networkRing.push({
            url: typeof args[0] === "string" ? args[0] : (args[0] as Request).url,
            method:
              (args[1] as RequestInit | undefined)?.method ??
              (typeof args[0] !== "string" ? (args[0] as Request).method : "GET"),
            status: res.status,
            durationMs: performance.now() - t0,
            timestamp: Date.now(),
            message: res.statusText,
          });
        }
        return res;
      } catch (err) {
        networkRing.push({
          url: typeof args[0] === "string" ? args[0] : (args[0] as Request).url,
          method:
            (args[1] as RequestInit | undefined)?.method ??
            (typeof args[0] !== "string" ? (args[0] as Request).method : "GET"),
          status: null,
          durationMs: performance.now() - t0,
          timestamp: Date.now(),
          message: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    };
  }

  // --- breadcrumbs ---
  // Walks up to 5 ancestors looking for an interactive element (button, link,
  // role=button, etc.) and extracts the most meaningful label from it. Avoids
  // dumping useless "click: div" entries when the user clicked padding inside
  // a card. Returns null when nothing meaningful was found — caller drops the
  // breadcrumb entirely rather than logging the raw tag.
  function resolveClickLabel(target: HTMLElement): string | null {
    const interactive = (el: HTMLElement) =>
      ["BUTTON", "A", "SUMMARY", "LABEL"].includes(el.tagName) ||
      el.hasAttribute("role") ||
      el.hasAttribute("aria-label") ||
      el.hasAttribute("data-testid") ||
      el.hasAttribute("data-label") ||
      (el as HTMLElement).onclick != null ||
      el.tabIndex >= 0;

    let el: HTMLElement | null = target;
    let hops = 0;
    while (el && hops < 5) {
      const aria = el.getAttribute("aria-label");
      if (aria) return aria.slice(0, 80);
      const dataLabel = el.getAttribute("data-label");
      if (dataLabel) return dataLabel.slice(0, 80);
      const testId = el.getAttribute("data-testid");
      if (testId && interactive(el)) return testId.slice(0, 80);
      if (interactive(el)) {
        const text = el.innerText?.trim().replace(/\s+/g, " ");
        if (text) return text.slice(0, 80);
        const title = el.getAttribute("title");
        if (title) return title.slice(0, 80);
        const role = el.getAttribute("role");
        if (role) return `${role}`;
        // Interactive but no text content (e.g. icon-only button): fall through
        // to next ancestor rather than returning the bare tag name.
      }
      el = el.parentElement;
      hops += 1;
    }
    // Last-resort: own innerText, only if non-empty and short enough to be useful.
    const text = target.innerText?.trim().replace(/\s+/g, " ");
    if (text && text.length > 0) return text.slice(0, 80);
    return null;
  }

  window.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const label = resolveClickLabel(target);
      if (!label) return;
      breadcrumbs.push({ type: "click", label, timestamp: Date.now() });
    },
    { capture: true, passive: true },
  );

  window.addEventListener("popstate", () => {
    breadcrumbs.push({ type: "nav", label: location.pathname, timestamp: Date.now() });
  });

  // --- unhandled errors → console ring ---
  window.addEventListener("error", (e) => {
    consoleRing.push({
      level: "error",
      message: `[window.error] ${e.message} @ ${e.filename}:${e.lineno}`,
      timestamp: Date.now(),
    });
  });

  return {
    breadcrumb(type: Breadcrumb["type"], label: string) {
      breadcrumbs.push({ type, label, timestamp: Date.now() });
    },
    snapshot() {
      const ua = navigator.userAgent;
      return {
        osName: detectOS(ua),
        browserName: detectBrowser(ua),
        consoleLogs: consoleRing.snapshot(),
        networkErrors: networkRing.snapshot(),
        breadcrumbs: breadcrumbs.snapshot(),
      };
    },
  };
}
